// Permission groups (PHASE23): named sets of rights per portal section (view, add, edit, delete and a section's
// own actions), given to agents and phone users on the Users page (2026-10-15: the supervisor user type is retired). The API (backend/src/perms.ts) enforces them.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Copy, Eye } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { PageHeader, DataTable, ErrorBox, Field } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/store/toast';
import { useCan } from '@/store/auth';
import { useGroups, type PermissionGroup } from './Admin';
import { t } from '@/i18n';

interface Section { key: string; label: string; group: string; actions: string[] }
const MAIN = ['view', 'add', 'edit', 'delete'];
const ACTION_LABEL: Record<string, string> = {
  view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete', control: 'Start / stop', import: 'Import', export: 'Export',
  play: 'Play', hangup: 'Hang up calls', barge: 'Listen / whisper / barge', agent_state: 'Change agent state',
};
// catalog texts (section labels, areas, action names) come from the API in English; translated where shown
const actionText = (a: string) => t(ACTION_LABEL[a] ?? a);
// the built-in Supervisor group (name and description from the API) is shown translated
const groupName = (g: PermissionGroup) => (g.builtin ? t(g.name) : g.name);

function useCatalog() {
  return useQuery({ queryKey: ['permission-catalog'], queryFn: () => api<{ sections: Section[]; supervisor: string[] }>('/permissions/catalog'), staleTime: Infinity });
}

export function PermissionGroupsPage() {
  const groups = useGroups();
  const [canAdd, canEdit, canDelete] = [useCan('permission_groups.add'), useCan('permission_groups.edit'), useCan('permission_groups.delete')];
  // undefined: closed; { group: null }: new; copyOf: a new group starting from another's rights
  const [edit, setEdit] = React.useState<{ group: PermissionGroup | null; copyOf?: PermissionGroup; readOnly?: boolean } | undefined>(undefined);
  async function remove(g: PermissionGroup) {
    if (!confirm(t('Delete the group "{{name}}"?', { name: g.name }))) return;
    try { await api(`/permission-groups/${g.id}`, { method: 'DELETE' }); toast.ok(t('Deleted')); groups.refetch(); } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div>
      <PageHeader title={t('Permission groups')}
        description={t('Rights added to agents and phone users, section by section. A supervisor is a user in the group Supervisor; admins can do everything.')}>
        {canAdd && <Button onClick={() => setEdit({ group: null })}><Plus /> {t('New group')}</Button>}
      </PageHeader>
      <ErrorBox error={groups.error} />
      <DataTable rowKey={(g) => g.id} rows={groups.data} columns={[
        { key: 'name', label: t('Name'), render: (g) => <span className="font-medium">{groupName(g)}</span> },
        { key: 'description', label: t('Description'), render: (g) => (g.builtin && g.description ? t(g.description) : g.description) },
        { key: 'permissions', label: t('Rights'), render: (g) => g.permissions.length },
        { key: 'users', label: t('Users'), render: (g) => g.users }]}
        actions={(g) => (<div className="flex justify-end gap-1">
          {g.builtin || !canEdit
            ? <Button size="icon" variant="ghost" aria-label={t('View {{name}}', { name: g.name })} onClick={() => setEdit({ group: g, readOnly: true })}><Eye /></Button>
            : <Button size="icon" variant="ghost" aria-label={t('Edit {{name}}', { name: g.name })} onClick={() => setEdit({ group: g })}><Pencil /></Button>}
          {canAdd && <Button size="icon" variant="ghost" aria-label={t('Copy {{name}}', { name: g.name })} title={t('Copy into a new group')} onClick={() => setEdit({ group: null, copyOf: g })}><Copy /></Button>}
          {canDelete && !g.builtin && <Button size="icon" variant="ghost" aria-label={t('Delete {{name}}', { name: g.name })} onClick={() => remove(g)}><Trash2 className="text-destructive" /></Button>}
        </div>)} />
      <Dialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)}>
        {edit && <DialogContent className="max-w-4xl"
          title={edit.readOnly ? groupName(edit.group!) : edit.group ? t('Edit {{name}}', { name: edit.group.name }) : edit.copyOf ? t('New group from {{name}}', { name: edit.copyOf.name }) : t('New permission group')}>
          <GroupForm group={edit.group} copyOf={edit.copyOf} readOnly={edit.readOnly} onDone={() => { setEdit(undefined); groups.refetch(); }} />
        </DialogContent>}
      </Dialog>
    </div>
  );
}

function GroupForm({ group, copyOf, readOnly, onDone }: { group: PermissionGroup | null; copyOf?: PermissionGroup; readOnly?: boolean; onDone: () => void }) {
  const cat = useCatalog();
  const [name, setName] = React.useState(group?.name ?? (copyOf ? `${copyOf.name.replace(/ \(built in\)$/, '')} (copy)` : ''));
  const [description, setDescription] = React.useState(group?.description ?? copyOf?.description ?? '');
  const [rights, setRights] = React.useState<Set<string>>(() => new Set(group?.permissions ?? copyOf?.permissions ?? []));
  const [error, setError] = React.useState<unknown>(null);

  // ticking anything in a section also ticks its View; unticking View clears the section
  function toggle(s: Section, a: string) {
    setRights((old) => {
      const n = new Set(old);
      const key = `${s.key}.${a}`;
      if (n.has(key)) {
        n.delete(key);
        if (a === 'view') for (const x of s.actions) n.delete(`${s.key}.${x}`);
      } else {
        n.add(key);
        if (s.actions.includes('view')) n.add(`${s.key}.view`);
      }
      return n;
    });
  }
  function setSection(s: Section, on: boolean) {
    setRights((old) => { const n = new Set(old); for (const a of s.actions) on ? n.add(`${s.key}.${a}`) : n.delete(`${s.key}.${a}`); return n; });
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const body = { name, description: description || null, permissions: [...rights].sort() };
    try {
      if (group) await api(`/permission-groups/${group.id}`, { method: 'PATCH', body });
      else await api('/permission-groups', { body });
      toast.ok(t('Group saved')); onDone();
    } catch (err) { setError(err); }
  }
  const sections = cat.data?.sections ?? [];
  const areas = [...new Set(sections.map((s) => s.group))];
  return (
    <form onSubmit={submit} className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field help="groups.name" label={t('Name')}><Input name="name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} disabled={readOnly} /></Field>
        <Field help="groups.description" label={t('Description')}><Input name="description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={255} disabled={readOnly} /></Field>
      </div>
      <ErrorBox error={cat.error} />
      <div className="max-h-[55vh] overflow-auto rounded-md border">
        <table className="w-full text-sm" data-testid="rights-grid">
          <thead className="sticky top-0 bg-muted">
            <tr><th className="px-2 py-1.5 text-left">{t('Section')}</th>{MAIN.map((a) => <th key={a} className="w-16 px-2 py-1.5">{actionText(a)}</th>)}<th className="px-2 py-1.5 text-left">{t('Other')}</th></tr>
          </thead>
          <tbody>
            {areas.map((area) => (<React.Fragment key={area}>
              <tr><td colSpan={6} className="bg-muted/40 px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">{t(area)}</td></tr>
              {sections.filter((s) => s.group === area).map((s) => {
                const all = s.actions.every((a) => rights.has(`${s.key}.${a}`));
                return (
                  <tr key={s.key} className="border-t" data-testid={`rights-${s.key}`}>
                    <td className="px-2 py-1">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={all} disabled={readOnly} onChange={() => setSection(s, !all)} aria-label={t('All of {{section}}', { section: t(s.label) })} /> {t(s.label)}
                      </label>
                    </td>
                    {MAIN.map((a) => (
                      <td key={a} className="px-2 py-1 text-center">
                        {s.actions.includes(a) && <input type="checkbox" checked={rights.has(`${s.key}.${a}`)} disabled={readOnly}
                          onChange={() => toggle(s, a)} aria-label={`${t(s.label)}: ${actionText(a)}`} data-right={`${s.key}.${a}`} />}
                      </td>
                    ))}
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-3">
                        {s.actions.filter((a) => !MAIN.includes(a)).map((a) => (
                          <label key={a} className="flex items-center gap-1">
                            <input type="checkbox" checked={rights.has(`${s.key}.${a}`)} disabled={readOnly} onChange={() => toggle(s, a)}
                              aria-label={`${t(s.label)}: ${actionText(a)}`} data-right={`${s.key}.${a}`} /> {actionText(a)}
                          </label>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("Pages that pick from another section (a campaign's queues, a queue's agents) need View on that section too.")}
        {' '}<Badge variant="outline">{t('{{n}} rights', { n: rights.size })}</Badge>
      </p>
      <ErrorBox error={error} />
      {!readOnly && <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>}

    </form>
  );
}
